import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { JonumBlockingReason } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BrandM } from '#src/atoms/typography/index.tsx';
import { Countdown } from '#src/components/Countdown/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';

import type {
  JonumComposeBlockedDialogQuery,
  JonumComposeBlockedDialogQueryVariables,
} from './__generated__/index.graphql.ts';

const JONUM_COMPOSE_BLOCKED_DIALOG_QUERY = gql`
  query JonumComposeBlockedDialogQuery {
    currentUser {
      slug
      jonumVerification {
        id
        selfExcludedUntil
        restrictions {
          compose {
            blocked
            reason
          }
        }
        playtime {
          periodResetsAt
        }
      }
    }
  }
` as TypedDocumentNode<
  JonumComposeBlockedDialogQuery,
  JonumComposeBlockedDialogQueryVariables
>;

const Content = styled(Vertical).attrs({ gap: 3, center: true })`
  padding: var(--quadruple-unit) var(--double-unit) var(--double-unit);
  text-align: center;
`;

type DialogContent = {
  endsAt: Date;
  title: ReactNode;
  description: ReactNode;
};

type Props = {
  open?: boolean;
};

export const JonumComposeBlockedDialog = ({ open = true }: Props) => {
  const navigate = useNavigate();
  const { data, refetch } = useQuery(JONUM_COMPOSE_BLOCKED_DIALOG_QUERY, {
    skip: !open,
  });

  const verification = data?.currentUser?.jonumVerification;
  const compose = verification?.restrictions?.compose;

  let content: DialogContent | null = null;

  if (compose?.blocked && verification) {
    if (
      compose.reason === JonumBlockingReason.PLAYTIME_EXHAUSTED &&
      verification.playtime.periodResetsAt
    ) {
      content = {
        endsAt: new Date(verification.playtime.periodResetsAt),
        title: (
          <FormattedMessage defaultMessage="Gameplay time limit reached" />
        ),
        description: (
          <FormattedMessage defaultMessage="You have reached your gameplay time limit. Access to Sorare is temporarily suspended until the next reset of the 7-day period." />
        ),
      };
    } else if (
      compose.reason === JonumBlockingReason.SELF_EXCLUDED &&
      verification.selfExcludedUntil
    ) {
      content = {
        endsAt: new Date(verification.selfExcludedUntil),
        title: <FormattedMessage defaultMessage="You are self-excluded" />,
        description: (
          <FormattedMessage defaultMessage="You are currently in a self-exclusion period. Access to Sorare will be automatically restored at the end of the selected period." />
        ),
      };
    }
  }

  if (!content) return null;

  return (
    <Dialog
      open={open}
      hideHeader
      maxWidth="xs"
      disableEscapeKeyDown
      disableBackdropClick
    >
      <Content>
        <BrandM color="var(--c-yellow-300)">{content.title}</BrandM>
        <Countdown date={content.endsAt} gap={2} onEnded={refetch} />
        <BodyM color="var(--c-nd-600)">{content.description}</BodyM>
        <Button
          size="medium"
          color="quaternary"
          fullWidth
          onClick={() => navigate(-1)}
        >
          <FormattedMessage defaultMessage="Go back" />
        </Button>
      </Content>
    </Dialog>
  );
};
